import React, { useEffect, useContext, useState, useRef, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  Tooltip,
  Divider,
  Grid,
  Paper,
  Stack,
  Popover,
} from "@mui/material";
import moment from "moment";
import ChargePoint from "../ChargePoint/ChargePoint";
import Connector from "../Connector/Connector";
import MessageSender from "../MessageSender/MessageSender";
import SettingsContext from "../../Context/SettingsContext";
import { pointStatus, connectedStatuses } from "../../Config/charge-point-settings";
import { MonitorHeartOutlined, Speed, Clear } from "@mui/icons-material";
import { logTypes } from "../../common/constants";
import { hasCentralSystemUrl } from "../../common/central-system-url";
import {
  createSimulator,
  getSimulator,
  toConnectorView,
} from "../../OCPP/simulator-session";

const LOG_TYPES = {
  socket: logTypes.socket,
  send: logTypes.send,
  receive: logTypes.message,
  request: logTypes.request,
  info: logTypes.info,
  error: logTypes.error,
};

const MAX_LOG_ENTRIES = 1000;
const logArray = [];

const toPointStatus = (state, simulator) => {
  switch (state) {
    case "accepted":
    case "pending":
      return simulator?.authorizedIdTag ? pointStatus.authorized : pointStatus.connected;
    case "connected":
      return pointStatus.connected;
    case "connecting":
      return pointStatus.connecting;
    case "error":
    case "rejected":
      return pointStatus.error;
    default:
      return pointStatus.disconnected;
  }
};

const Main = () => {
  const { settingsState } = useContext(SettingsContext);
  const [simulator, setSimulator] = useState(getSimulator());
  const [logs, setLogs] = useState(logArray);
  const [status, setStatus] = useState(toPointStatus(getSimulator()?.connectionState, getSimulator()));
  const [connectorViews, setConnectorViews] = useState({});
  const [upload, setUpload] = useState({ active: false, kind: "upload", label: "", secondsLeft: 0 });
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const [helpText, setHelpText] = useState("");
  const [autoReconnect, setAutoReconnect] = useState(true);

  const canConnect = hasCentralSystemUrl(settingsState.mainSettings.centralSystemUrl);
  const open = Boolean(helpAnchorEl);

  const logsEndRef = useRef(null);
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [logs]);

  const appendLog = useCallback((entry) => {
    logArray.push({
      time: moment(entry.time).format("HH:mm:ss"),
      type: LOG_TYPES[entry.type] ?? logTypes.info,
      command: entry.command,
      message: entry.message,
    });
    if (logArray.length > MAX_LOG_ENTRIES) logArray.splice(0, logArray.length - MAX_LOG_ENTRIES);
    setLogs([...logArray]);
  }, []);

  const clearLog = () => {
    logArray.length = 0;
    setLogs([]);
  };

  // Follow the current simulator's events.
  useEffect(() => {
    if (!simulator) return undefined;
    const refreshConnectors = () =>
      setConnectorViews(
        Object.fromEntries(
          simulator.evses.map((evse) => [evse.id, toConnectorView(evse, settingsState.mainSettings.RFIDTag, simulator.maxPowerW)])
        )
      );
    refreshConnectors();
    setStatus(toPointStatus(simulator.connectionState, simulator));
    const offs = [
      simulator.on("log", appendLog),
      simulator.on("state", (state) => setStatus(toPointStatus(state, simulator))),
      simulator.on("evse", refreshConnectors),
      simulator.on("upload", setUpload),
    ];
    return () => offs.forEach((off) => off());
  }, [simulator, appendLog, settingsState.mainSettings.RFIDTag]);

  // Saving settings disposes the simulator (see saveSettings); forget it here too.
  useEffect(() => {
    const onSettingsSaved = () => {
      setSimulator(null);
      setConnectorViews({});
      setStatus(pointStatus.disconnected);
    };
    window.addEventListener("ocpp-settings-saved", onSettingsSaved);
    return () => window.removeEventListener("ocpp-settings-saved", onSettingsSaved);
  }, []);

  const handleConnect = useCallback(() => {
    const next = getSimulator() ?? createSimulator(settingsState);
    next.settings.autoReconnect = autoReconnect;
    setSimulator(next);
    next.connect().catch((error) => appendLog({ time: new Date(), type: "error", message: error.message }));
  }, [settingsState, autoReconnect, appendLog]);

  const handleDisconnect = useCallback(() => {
    simulator?.disconnect();
  }, [simulator]);

  const handleToggleAutoReconnect = (value) => {
    setAutoReconnect(value);
    if (simulator) simulator.settings.autoReconnect = value;
  };

  /** Run a simulator action from the UI; failures are already logged by the simulator. */
  const run = useCallback(
    (action) => {
      Promise.resolve()
        .then(action)
        .catch((error) => appendLog({ time: new Date(), type: "error", message: error.message }));
    },
    [appendLog]
  );

  const showHelpText = (event, key) => {
    const value = simulator?.config.get(key) ??
      settingsState.stationSettings.find((x) => x.key === key)?.value;
    setHelpText(`${key} set to ${value} seconds`);
    setHelpAnchorEl(event.target);
  };

  const isConnected = connectedStatuses.includes(status.status);
  const numberOfConnectors = Number(settingsState.mainSettings.numberOfConnectors) || 1;

  return (
    <Container sx={{ maxWidth: "1366px !important" }}>
      <Grid container spacing={3}>
        <Grid item xs={3.2}>
          <ChargePoint
            status={status}
            protocol={simulator?.protocol ?? settingsState.mainSettings.OCPPversion}
            canConnect={canConnect}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            autoReconnect={autoReconnect}
            onToggleAutoReconnect={handleToggleAutoReconnect}
            onAuthorize={() => run(() => simulator.authorize(settingsState.mainSettings.RFIDTag))}
            onBootNotification={() => run(() => simulator.boot())}
            onHeartbeat={() => run(() => simulator.heartbeat())}
          />
          {upload.active ? (
            <Paper sx={{ mt: 3, p: 2, height: "42.5px" }}>
              <Box display="flex" alignItems="center">
                <img src="./sand.png" alt="upload animation" height={36} />
                <Typography variant="body2" ml={1} color="primary" sx={{ wordBreak: "break-all" }}>
                  {upload.kind === "download" ? "DOWNLOADING" : "UPLOADING"} {upload.label}
                </Typography>
                {upload.secondsLeft > 0 ? (
                  <Typography variant="body1" ml={0.5} color="primary">
                    in {upload.secondsLeft}s
                  </Typography>
                ) : null}
              </Box>
            </Paper>
          ) : null}
        </Grid>
        {[1, 2].map((id) => (
          <Grid item xs={4.4} key={id}>
            {id <= numberOfConnectors && connectorViews[id] ? (
              <Connector
                id={id}
                status={status}
                settings={connectorViews[id]}
                onStartTransaction={() => run(() => simulator.startTransaction(id))}
                onStopTransaction={() => run(() => simulator.stopTransaction(id))}
                onStatusNotification={(value) => run(() => simulator.setStatus(id, value))}
                onSendMeterValues={() => run(() => simulator.sendMeterValues(id))}
                onChange={(field, value) => {
                  if (field === "stopReason") simulator.setStopReason(id, value);
                  if (field === "currentMeterValue") simulator.setMeter(id, { meterWh: value });
                  if (field === "soc") simulator.setMeter(id, { soc: value });
                  if (field === "requestedPowerW") simulator.setPower(id, value);
                }}
              />
            ) : null}
          </Grid>
        ))}
        {simulator && isConnected ? (
          <Grid item xs={12}>
            <MessageSender simulator={simulator} onError={(message) => appendLog({ time: new Date(), type: "error", message })} />
          </Grid>
        ) : null}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignContent="center">
              <Typography variant="h6" color="primary">
                LOG
              </Typography>
              <Box display="flex" justifyContent="flex-end" alignContent="center">
                <Speed
                  sx={{ ml: 1, cursor: "pointer" }}
                  color="primary"
                  onClick={(event) => showHelpText(event, "MeterValueSampleInterval")}
                />
                <MonitorHeartOutlined
                  sx={{ ml: 1, cursor: "pointer" }}
                  color="primary"
                  onClick={(event) => showHelpText(event, "HeartbeatInterval")}
                />
                <Tooltip title="Clear log" placement="top" arrow>
                  <Clear sx={{ ml: 1, cursor: "pointer" }} color="primary" onClick={clearLog} />
                </Tooltip>
              </Box>
            </Box>
            <Divider sx={{ mt: 0.5, mb: 1.5 }} />
            <Stack spacing={1} height={340} maxHeight={295} sx={{ overflowY: "scroll", fontSize: 14 }}>
              {logs.map((el, index) => (
                <Stack
                  key={index}
                  direction="row"
                  color={el.type.color}
                  spacing={2}
                  divider={<Divider orientation="vertical" flexItem />}
                >
                  <Box>{el.time}</Box>
                  <Box width={55} minWidth={55}>
                    {el.type.text}
                  </Box>
                  <Box width={175} minWidth={175}>
                    {el.command}
                  </Box>
                  <Box sx={{ wordBreak: "break-all" }}>{el.message}</Box>
                </Stack>
              ))}
              <Box ref={logsEndRef} />
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      <Popover
        open={open}
        anchorEl={helpAnchorEl}
        onClose={() => setHelpAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Typography sx={{ p: 2, backgroundColor: "black", color: "white" }}>{helpText}</Typography>
      </Popover>
    </Container>
  );
};

export default Main;
